import React from "react";
import { Button, ButtonProps } from "@chakra-ui/react";
import { Link as RouterLink } from "react-router-dom";
import { useIndustry } from "./tokens";

type Variant = "primary" | "secondary" | "ghost";

interface IndustryButtonProps extends Omit<ButtonProps, "variant" | "title"> {
  variant?: Variant;
  /** Required: every control explains what it does (module hardening rule 19). */
  title: string;
  /** In-app route (react-router) … */
  to?: string;
  /** … or an external href (opens in the same tab). */
  href?: string;
  /** Reversed palette for steel fields. */
  onSteel?: boolean;
}

/** `.btn` from handoff/components.md — square, one colour, no shadow. */
export const IndustryButton: React.FC<IndustryButtonProps> = ({
  variant = "primary",
  title,
  to,
  href,
  onSteel = false,
  children,
  ...rest
}) => {
  const t = useIndustry();
  const palette =
    variant === "primary"
      ? onSteel
        ? { bg: t.inverse, color: t.steel, borderColor: t.inverse, _hover: { bg: t.accent200 } }
        : { bg: t.accent, color: t.inverse, borderColor: t.accent, _hover: { bg: t.accent700, borderColor: t.accent700 } }
      : variant === "secondary"
        ? onSteel
          ? { bg: "transparent", color: t.inverse, borderColor: t.steelRule, _hover: { bg: t.steelRule } }
          : { bg: "transparent", color: t.text, borderColor: t.rule, _hover: { bg: t.surface } }
        : { bg: "transparent", color: onSteel ? t.accent200 : t.accent700, borderColor: "transparent", _hover: { textDecoration: "underline" } };

  const linkProps = to ? { as: RouterLink, to } : href ? { as: "a" as const, href } : {};

  return (
    <Button
      {...(linkProps as ButtonProps)}
      title={title}
      borderRadius={0}
      borderWidth="1px"
      borderStyle="solid"
      fontWeight={500}
      fontSize="15px"
      h="auto"
      minH="40px"
      px="20px"
      py="8px"
      whiteSpace="normal"
      _active={{ opacity: 0.9 }}
      _focusVisible={{ boxShadow: `0 0 0 2px ${t.accent200}` }}
      {...palette}
      {...rest}
    >
      {children}
    </Button>
  );
};
